import React, { useState, useMemo, useCallback } from 'react';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { ErrorBoundary } from './ErrorBoundary';
import {
  Maximize2,
  Minimize2,
  Eye,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface EnhancedTableViewerProps {
  data: any[];
  className?: string;
  pageSize?: number; // Number of rows per page (default: 100)
}

interface CellViewerProps {
  content: string;
  isOpen: boolean;
  onClose: () => void;
}

const CellViewer: React.FC<CellViewerProps> = ({ content, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // console.error('Failed to copy text: ', err);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span>Cell Content</span>
            <Button
              variant="outline"
              size="sm"
              onClick={copyToClipboard}
              className="ml-2"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </>
              )}
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg overflow-auto max-h-[60vh]">
          <pre className="whitespace-pre-wrap text-sm font-mono">{content}</pre>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const EnhancedTableViewer: React.FC<EnhancedTableViewerProps> = ({
  data,
  className,
  pageSize: initialPageSize = 100
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{ content: string; isOpen: boolean }>({
    content: '',
    isOpen: false
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const headers = useMemo(() => {
    return data && data.length > 0 ? Object.keys(data[0]) : [];
  }, [data]);

  // Calculate pagination
  const totalRows = data?.length || 0;
  const totalPages = Math.ceil(totalRows / pageSize);
  const startIndex = currentPage * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRows);

  // Get paginated data
  const paginatedData = useMemo(() => {
    return data?.slice(startIndex, endIndex) || [];
  }, [data, startIndex, endIndex]);

  const handleCellClick = useCallback((content: string) => {
    if (content && content.length > 50) {
      setSelectedCell({ content, isOpen: true });
    }
  }, []);

  // Reset to first page when data changes
  useMemo(() => {
    setCurrentPage(0);
  }, [data]);

  if (!data || data.length === 0) return null;

  const TableContent = () => (
    <div className={cn("flex flex-col", isFullscreen ? "h-full" : "space-y-4")}>
      {/* Header with info and controls */}
      <div className={cn("flex items-center justify-between", isFullscreen ? "mb-2" : "")}>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {totalRows.toLocaleString()} total rows × {headers.length} columns
          {totalRows > pageSize && (
            <span className="ml-2 text-xs">
              (showing {startIndex + 1}–{endIndex})
            </span>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="h-8 w-8 p-0"
        >
          {isFullscreen ? (
            <Minimize2 className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Table */}
      <div className={cn(
        "border rounded-lg overflow-hidden",
        isFullscreen ? "flex-1 h-[calc(98vh-120px)]" : "max-h-96",
        "overflow-auto"
      )}>
        <Table>
          <TableHeader className="sticky top-0 bg-white dark:bg-gray-900 z-10">
            <TableRow>
              {headers.map((header) => (
                <TableHead key={header} className="font-semibold border-r last:border-r-0">
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={startIndex + index}>
                {headers.map((header) => {
                  // Safely convert cell content to string, handling objects
                  let cellContent = '';
                  try {
                    const cellValue = row[header];
                    if (cellValue === null || cellValue === undefined) {
                      cellContent = '';
                    } else if (typeof cellValue === 'object') {
                      cellContent = JSON.stringify(cellValue);
                    } else {
                      cellContent = String(cellValue);
                    }
                  } catch (error) {
                    cellContent = 'Error displaying value';
                  }

                  const displayContent = cellContent.length > 50
                    ? cellContent.substring(0, 50) + '...'
                    : cellContent;

                  return (
                    <TableCell
                      key={header}
                      className="border-r last:border-r-0 max-w-xs truncate cursor-pointer hover:bg-muted/50"
                      onClick={() => handleCellClick(cellContent)}
                    >
                      {displayContent}
                      {cellContent.length > 50 && (
                        <Eye className="inline ml-2 h-3 w-3 text-gray-400" />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2 py-3 border-t">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Page {currentPage + 1} of {totalPages.toLocaleString()}
          </div>

          <div className="flex items-center gap-2">
            {/* First Page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className="h-8 w-8 p-0"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>

            {/* Previous Page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {/* Page Number Display */}
            <div className="flex items-center gap-1 text-sm">
              <span className="text-gray-600 dark:text-gray-400">Go to page:</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage + 1}
                onChange={(e) => {
                  const page = parseInt(e.target.value) - 1;
                  if (page >= 0 && page < totalPages) {
                    setCurrentPage(page);
                  }
                }}
                className="w-16 px-2 py-1 text-center border rounded text-sm dark:bg-gray-800 dark:border-gray-700"
              />
            </div>

            {/* Next Page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage >= totalPages - 1}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            {/* Last Page */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className="h-8 w-8 p-0"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Rows per page selector */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600 dark:text-gray-400">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const newPageSize = parseInt(e.target.value);
                setPageSize(newPageSize);
                setCurrentPage(0); // Reset to first page when changing page size
              }}
              className="px-2 py-1 border rounded dark:bg-gray-800 dark:border-gray-700"
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="250">250</option>
              <option value="500">500</option>
              <option value="1000">1000</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <ErrorBoundary>
      <div className={cn("w-full", className)}>
        {isFullscreen ? (
          <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 p-4">
            <TableContent />
          </div>
        ) : (
          <TableContent />
        )}

        <CellViewer
          content={selectedCell.content}
          isOpen={selectedCell.isOpen}
          onClose={() => setSelectedCell({ content: '', isOpen: false })}
        />
      </div>
    </ErrorBoundary>
  );
};